import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface PlatformAnnouncement {
  id: string;
  title: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'MAINTENANCE' | 'FEATURE' | 'CRITICAL';
  targetAudience: 'ALL' | 'CREATORS' | 'PATRONS' | 'ADMINS';
  isActive: boolean;
  startDate: Date;
  endDate?: Date;
  dismissible: boolean;
  actionUrl?: string;
  actionText?: string;
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateAnnouncementData {
  title: string;
  message: string;
  type: 'INFO' | 'WARNING' | 'MAINTENANCE' | 'FEATURE' | 'CRITICAL';
  targetAudience: 'ALL' | 'CREATORS' | 'PATRONS' | 'ADMINS';
  isActive: boolean;
  startDate: string;
  endDate?: string;
  dismissible: boolean;
  actionUrl?: string;
  actionText?: string;
}

export interface UpdateAnnouncementData extends Partial<CreateAnnouncementData> {}

const announcementService = {
  // Create announcement
  create: async (data: CreateAnnouncementData): Promise<PlatformAnnouncement> => {
    const response = await axios.post(`${API_URL}/api/admin/announcements`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get all announcements (admin)
  getAll: async (params?: {
    isActive?: boolean;
    type?: string;
    targetAudience?: string;
  }): Promise<PlatformAnnouncement[]> => {
    const response = await axios.get(`${API_URL}/api/admin/announcements`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get active announcements (public)
  getActive: async (): Promise<PlatformAnnouncement[]> => {
    const response = await axios.get(`${API_URL}/api/announcements/active`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get single announcement
  getById: async (id: string): Promise<PlatformAnnouncement> => {
    const response = await axios.get(`${API_URL}/api/admin/announcements/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Update announcement
  update: async (id: string, data: UpdateAnnouncementData): Promise<PlatformAnnouncement> => {
    const response = await axios.patch(`${API_URL}/api/admin/announcements/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Delete announcement
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/api/admin/announcements/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },

  // Toggle announcement active status
  toggle: async (id: string): Promise<PlatformAnnouncement> => {
    const response = await axios.patch(
      `${API_URL}/api/admin/announcements/${id}/toggle`,
      {},
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },
};

export default announcementService;
